// frontend/src/components/ScheduleConflictDialog.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  CircularProgress,
  CardMedia
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import { format } from 'date-fns';
import apiClient from '../api/client';

const ScheduleConflictDialog = ({
  open,
  onClose,
  conflicts,
  onCancelExistingAndProceed,
  isCancelling = false
}) => {
  if (!conflicts || conflicts.length === 0) return null;

  const totalConflicts = conflicts.reduce(
    (sum, c) => sum + c.existingSubmissions.length, 0
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningAmberIcon color="warning" />
          <Typography variant="h6">Existing Scheduled Submissions Found</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {totalConflicts === 1
            ? 'There is an existing scheduled submission for one of the images you are trying to submit.'
            : `There are ${totalConflicts} existing scheduled submissions for the images you are trying to submit.`
          }
          {' '}Proceeding will cancel the existing scheduled submission{totalConflicts !== 1 ? 's' : ''} and replace {totalConflicts !== 1 ? 'them' : 'it'} with your new submission.
        </Alert>

        <List sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {conflicts.map((conflict, idx) => (
            <React.Fragment key={conflict.image.id}>
              {idx > 0 && <Divider sx={{ my: 1 }} />}
              <ListItem alignItems="flex-start" sx={{ flexDirection: 'column', gap: 1 }}>
                <Box display="flex" gap={2} width="100%">
                  <CardMedia
                    component="img"
                    sx={{
                      width: 120,
                      height: 120,
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.50'
                    }}
                    image={
                      conflict.image.file_path
                        ? `${apiClient.defaults.baseURL}/images/${conflict.image.id}/thumbnail?size=400&t=${new Date(conflict.image.updated_at || conflict.image.created_at).getTime()}`
                        : 'https://placehold.co/400x400/eee/ccc?text=No+Preview'
                    }
                    alt={`GTIN ${conflict.image.gtin}`}
                    onError={(e) => { e.target.src = 'https://placehold.co/400x400/eee/ccc?text=No+Preview'; }}
                  />
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      GTIN: {conflict.image.gtin}
                    </Typography>
                    {conflict.image.brand && (
                      <Typography variant="body2" color="text.secondary">
                        {conflict.image.brand} &mdash; {conflict.image.description || conflict.image.flavor || ''}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1 }}>
                      {conflict.existingSubmissions.map((sub) => (
                        <Box
                          key={sub.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 0.5,
                            p: 1,
                            bgcolor: 'warning.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'warning.200'
                          }}
                        >
                          <ScheduleIcon fontSize="small" color="warning" />
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2">
                                  Scheduled for: {format(new Date(sub.scheduled_for), 'MMM d, yyyy h:mm a')}
                                </Typography>
                                {sub.is_badge_removal && (
                                  <Chip label="Badge Removal" size="small" color="info" variant="outlined" />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                Scheduled by: {sub.user_email || sub.user_username || 'Unknown'}
                              </Typography>
                            }
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isCancelling} color="inherit">
          Keep Existing
        </Button>
        <Button
          onClick={onCancelExistingAndProceed}
          variant="contained"
          color="warning"
          disabled={isCancelling}
          startIcon={isCancelling ? <CircularProgress size={18} color="inherit" /> : <CancelScheduleSendIcon />}
        >
          {isCancelling ? 'Cancelling...' : `Cancel Existing & Proceed`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleConflictDialog;
